import type { ManiaEngineInput } from '@shared/engine/mania/core';
import type { ManiaLane } from '@shared/maniaNote';
import {
  loadKeybindPrefs,
  maniaLaneForCode,
  type KeybindPrefs,
} from '../keybindPrefs';

/**
 * Common contract for mania-style input sources (Piano, Guitar, and
 * any future 4-lane falling-notes mode). Mirrors {@link
 * import('../circles/circlesInputSource.js').CirclesInputSource} so
 * session loops look identical across modes; the only differences
 * are the engine-input shape and the extra `getPressedLanes` accessor
 * that the renderer reads to draw a key-glow on currently held lanes.
 */
export interface ManiaInputSource {
  readonly kind: 'keyboard' | 'replay';
  /**
   * Drain queued inputs that landed at or before `currentBeat`. Live
   * keyboard / pointer sources stamp inputs with the wall-clock beat
   * they arrived at and ignore the parameter; the replay source uses
   * it to gate inputs by the simulator's progressing beat.
   */
  poll: (currentBeat: number) => ManiaEngineInput[];
  /** Lanes currently held down — purely for renderer key-glow feedback. */
  getPressedLanes: () => ReadonlySet<ManiaLane>;
  destroy: () => void;
}

/**
 * Per-mode wiring needed by {@link KeyboardManiaInputSource}. Lets a
 * single live keyboard / pointer implementation serve both Piano
 * (flat lanes via `pianoPlayfieldGeometry.laneFromClientX`) and Guitar
 * (perspective lanes via `guitarPlayfieldGeometry.laneFromClientX`)
 * without code duplication. The mode flag picks which slice of
 * {@link KeybindPrefs} drives keyboard lookups (`pianoLanes` vs
 * `guitarLanes`).
 */
export interface KeyboardManiaInputConfig {
  mode: 'piano' | 'guitar';
  canvas: HTMLCanvasElement;
  laneFromClientX: (canvas: HTMLCanvasElement, clientX: number) => ManiaLane | null;
  getBeatAtNow: () => number;
  isClockRunning?: () => boolean;
}

const EMPTY_LANE_SET: ReadonlySet<ManiaLane> = new Set();

/**
 * Live input source for keyboard + multi-touch / mouse on a 4-lane
 * mania-style playfield. Queues {@link ManiaEngineInput} events as
 * they arrive and drains them on each poll call. Lanes carry both
 * `down` and `up` events so the engine can resolve hold notes on
 * release.
 *
 * Pointer model:
 *   - One pointer per lane at most. A pointerdown on lane 1 while
 *     pointer 0 is still in lane 1 is folded into a "still held" state
 *     (no second `down` event) — multi-touch with finger drift across
 *     lane boundaries shouldn't spam the queue with phantom presses.
 *
 * Keyboard model:
 *   - Each {@link KeyboardEvent.code} is tracked independently. Holding
 *     'q' while pressing 'w' produces two simultaneous presses on
 *     lanes 0 and 1. Auto-repeat is suppressed via {@link
 *     KeyboardEvent.repeat} so a held key fires exactly one `down`
 *     and one `up` event.
 *
 * Mode-specific surfaces (lane geometry for pointer projection, key
 * binding lookup) are passed in via {@link KeyboardManiaInputConfig}
 * so Piano and Guitar share this implementation byte-for-byte while
 * each retaining their own spatial + keybind contract.
 */
export class KeyboardManiaInputSource implements ManiaInputSource {
  readonly kind = 'keyboard' as const;
  private queue: ManiaEngineInput[] = [];
  private cfg: KeyboardManiaInputConfig;
  private active = true;
  /** Set of currently-held source ids (`key-KeyQ`, `pointer-3`). */
  private heldSources = new Set<string>();
  /** Lane each pointer / key is currently associated with (for clean release). */
  private pointerLanes = new Map<number, ManiaLane>();
  /** Lane each key code is currently mapped to (resolved at keydown). */
  private keyLanes = new Map<string, ManiaLane>();
  private keyLanesPressed = new Set<ManiaLane>();
  private pointerLanesPressed = new Set<ManiaLane>();
  /**
   * Snapshot of keybind prefs captured at construction. Rebinds made
   * via the settings UI take effect on the next session start (which
   * is what the in-game Restart button triggers).
   */
  private keybindPrefs: KeybindPrefs;

  constructor(cfg: KeyboardManiaInputConfig) {
    this.cfg = cfg;
    this.keybindPrefs = loadKeybindPrefs();
    cfg.canvas.addEventListener('pointerdown', this.onPointerDown);
    cfg.canvas.addEventListener('pointerup', this.onPointerUp);
    cfg.canvas.addEventListener('pointercancel', this.onPointerUp);
    cfg.canvas.addEventListener('pointerleave', this.onPointerUp);
    cfg.canvas.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onWindowBlur);
  }

  private isClockRunning(): boolean {
    return this.cfg.isClockRunning ? this.cfg.isClockRunning() : true;
  }

  private resolveLaneForKey(code: string): ManiaLane | null {
    const lane = maniaLaneForCode(this.keybindPrefs, this.cfg.mode, code);
    return lane === null ? null : (lane as ManiaLane);
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (!this.active) {
      return;
    }
    if (e.pointerType === 'mouse' && e.button !== 0) {
      return;
    }
    e.preventDefault();
    const lane = this.cfg.laneFromClientX(this.cfg.canvas, e.clientX);
    if (lane === null) {
      return;
    }
    this.pointerLanes.set(e.pointerId, lane);
    this.heldSources.add(`pointer-${e.pointerId}`);
    this.pointerLanesPressed.add(lane);
    if (!this.isClockRunning()) {
      return;
    }
    this.queue.push({ beat: this.cfg.getBeatAtNow(), lane, type: 'down', source: 'pointer' });
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (!this.active) {
      return;
    }
    const lane = this.pointerLanes.get(e.pointerId);
    if (lane === undefined) {
      return;
    }
    this.pointerLanes.delete(e.pointerId);
    this.heldSources.delete(`pointer-${e.pointerId}`);
    // Recompute aggregate pressed-lanes set from the remaining held
    // pointers so a pointer release in lane 0 doesn't drop the lane
    // glow if a *different* pointer is also down on lane 0.
    this.pointerLanesPressed.clear();
    for (const heldLane of this.pointerLanes.values()) {
      this.pointerLanesPressed.add(heldLane);
    }
    if (!this.isClockRunning()) {
      return;
    }
    this.queue.push({ beat: this.cfg.getBeatAtNow(), lane, type: 'up', source: 'pointer' });
  };

  private onContextMenu = (e: MouseEvent): void => {
    if (!this.active) {
      return;
    }
    // Right-click on the canvas could be a stray tap during play —
    // suppress the browser context menu so it doesn't break flow.
    e.preventDefault();
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.active) {
      return;
    }
    const lane = this.resolveLaneForKey(e.code);
    if (lane === null) {
      return;
    }
    if (e.repeat) {
      return;
    }
    e.preventDefault();
    const sourceId = `key-${e.code}`;
    if (this.heldSources.has(sourceId)) {
      return;
    }
    this.heldSources.add(sourceId);
    // Remember the lane this code was mapped to at press time so
    // keyup releases the right lane even if the binding has been
    // edited mid-keypress (rebinds only take effect next session,
    // but we still want defensive correctness here).
    this.keyLanes.set(e.code, lane);
    this.keyLanesPressed.add(lane);
    if (!this.isClockRunning()) {
      return;
    }
    this.queue.push({ beat: this.cfg.getBeatAtNow(), lane, type: 'down', source: 'keyboard' });
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (!this.active) {
      return;
    }
    const lane = this.keyLanes.get(e.code);
    if (lane === undefined) {
      return;
    }
    const sourceId = `key-${e.code}`;
    if (!this.heldSources.has(sourceId)) {
      return;
    }
    this.heldSources.delete(sourceId);
    this.keyLanes.delete(e.code);
    // Recompute aggregate key-pressed lanes from the surviving held
    // codes so releasing 'q' while 'd' is still held keeps lane 0
    // glowing in the renderer.
    this.keyLanesPressed.clear();
    for (const heldLane of this.keyLanes.values()) {
      this.keyLanesPressed.add(heldLane);
    }
    if (!this.isClockRunning()) {
      return;
    }
    this.queue.push({ beat: this.cfg.getBeatAtNow(), lane, type: 'up', source: 'keyboard' });
  };

  private onWindowBlur = (): void => {
    // OS-level focus loss (Alt-Tab, switch desktop, OS modal) does NOT
    // fire the visibilitychange auto-pause path — the session keeps
    // running in the background. If we just cleared the held maps,
    // the engine would still believe lanes were pressed (bricking any
    // hold-tail in flight) and the captured replay would have unpaired
    // `down` events. Synthesise a matching `up` per held lane first so
    // the engine and replay stay consistent, mirroring what a real
    // keyup/pointerup would have produced.
    if (this.isClockRunning()) {
      const beat = this.cfg.getBeatAtNow();
      for (const lane of this.keyLanes.values()) {
        this.queue.push({ beat, lane, type: 'up', source: 'keyboard' });
      }
      for (const lane of this.pointerLanes.values()) {
        this.queue.push({ beat, lane, type: 'up', source: 'pointer' });
      }
    }
    this.heldSources.clear();
    this.keyLanes.clear();
    this.keyLanesPressed.clear();
    this.pointerLanesPressed.clear();
    this.pointerLanes.clear();
  };

  poll(_currentBeat: number): ManiaEngineInput[] {
    if (this.queue.length === 0) {
      return [];
    }
    const out = this.queue;
    this.queue = [];
    return out;
  }

  getPressedLanes(): ReadonlySet<ManiaLane> {
    if (this.keyLanesPressed.size === 0 && this.pointerLanesPressed.size === 0) {
      return EMPTY_LANE_SET;
    }
    const merged = new Set<ManiaLane>();
    for (const l of this.keyLanesPressed) {
      merged.add(l);
    }
    for (const l of this.pointerLanesPressed) {
      merged.add(l);
    }
    return merged;
  }

  destroy(): void {
    this.active = false;
    this.heldSources.clear();
    this.keyLanes.clear();
    this.keyLanesPressed.clear();
    this.pointerLanesPressed.clear();
    this.pointerLanes.clear();
    this.cfg.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.cfg.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.cfg.canvas.removeEventListener('pointercancel', this.onPointerUp);
    this.cfg.canvas.removeEventListener('pointerleave', this.onPointerUp);
    this.cfg.canvas.removeEventListener('contextmenu', this.onContextMenu);
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onWindowBlur);
  }
}

/**
 * Replay input source for headless / verifier flows. Mirrors
 * {@link import('../circles/circlesInputSource.js').CirclesReplayInputSource}.
 * Tracks pressed lanes across `down` / `up` events so the renderer
 * still gets accurate key-glow during deterministic replays.
 *
 * Mode-agnostic — the engine only needs `lane` + `type` + `beat`,
 * and the lane semantics are identical between Piano and Guitar.
 */
export class ReplayManiaInputSource implements ManiaInputSource {
  readonly kind = 'replay' as const;
  private inputs: ManiaEngineInput[];
  private cursor = 0;
  private pressed = new Set<ManiaLane>();

  constructor(inputs: ManiaEngineInput[]) {
    this.inputs = [...inputs].sort((a, b) => {
      if (a.beat !== b.beat) {
        return a.beat - b.beat;
      }
      // `down` before `up` for stable ordering at coincident beats.
      if (a.type !== b.type) {
        return a.type === 'down' ? -1 : 1;
      }
      return a.lane - b.lane;
    });
  }

  poll(currentBeat: number): ManiaEngineInput[] {
    const out: ManiaEngineInput[] = [];
    while (this.cursor < this.inputs.length && this.inputs[this.cursor].beat <= currentBeat) {
      const inp = this.inputs[this.cursor];
      if (inp.type === 'down') {
        this.pressed.add(inp.lane);
      } else {
        this.pressed.delete(inp.lane);
      }
      out.push(inp);
      this.cursor += 1;
    }
    return out;
  }

  getPressedLanes(): ReadonlySet<ManiaLane> {
    return this.pressed;
  }

  destroy(): void {
    this.inputs = [];
    this.cursor = 0;
    this.pressed.clear();
  }

  isExhausted(): boolean {
    return this.cursor >= this.inputs.length;
  }
}
